/**
 * A tiling value-noise texture for the mist. Cosmetic: it never touches the core, and it only
 * needs to be smooth, seamless and the same on every platform that wants the same look.
 */
import { mix32 } from "@mistsweeper/core";

export const NOISE_SIZE = 220;
export const NOISE_CELL = 22;
const CELLS = NOISE_SIZE / NOISE_CELL;

function smooth(t: number): number {
  return t * t * (3 - 2 * t);
}

/** One byte per pixel, NOISE_SIZE square, wrapping seamlessly on both axes. */
export function makeNoiseTexture(salt = 911): Uint8Array {
  const corner = new Float32Array(CELLS * CELLS);
  for (let j = 0; j < CELLS; j += 1) {
    for (let i = 0; i < CELLS; i += 1) corner[j * CELLS + i] = mix32(i, j, salt) / 4294967296;
  }
  const out = new Uint8Array(NOISE_SIZE * NOISE_SIZE);
  for (let y = 0; y < NOISE_SIZE; y += 1) {
    const cj = Math.floor(y / NOISE_CELL);
    const v = smooth((y - cj * NOISE_CELL) / NOISE_CELL);
    const cj1 = (cj + 1) % CELLS;
    for (let x = 0; x < NOISE_SIZE; x += 1) {
      const ci = Math.floor(x / NOISE_CELL);
      const u = smooth((x - ci * NOISE_CELL) / NOISE_CELL);
      const ci1 = (ci + 1) % CELLS;
      const a = corner[cj * CELLS + ci] as number;
      const b = corner[cj * CELLS + ci1] as number;
      const c = corner[cj1 * CELLS + ci] as number;
      const d = corner[cj1 * CELLS + ci1] as number;
      const n = (a + (b - a) * u) * (1 - v) + (c + (d - c) * u) * v;
      out[y * NOISE_SIZE + x] = Math.round(n * 255);
    }
  }
  return out;
}
